import { useQuery } from "@tanstack/react-query";
import {
  activityPriorIncident,
  actualVsProjectedFall,
  careGiversReportingIncidents,
  fallIncidentPlace,
  getAnalyticsIncidentList,
  getAnalyticsIncidentTrend,
  getAnalyticsRepeatIncidents,
  getAnalyticsStatusReport,
  getAnalyticsWoundAnalysis,
  getAnalyticsBehaviorAnalysis,
  getAnalyticsInjuryReport,

  incidentListAnalysis,
  incidentRateAnalysis,
  overviewDayOfWeekFallIncidentAnalytis,
  overviewOfShiftAndUnitFallIncidents,
  overviewRepeatfallsIncidents,
  repeatFallAnalysis,
  overviewTimeOfIncident2HourSpans,
  daysOfWeekDetails,
  placeOfIncidentDetails,
  caregiversReportingDetails,
  repeatFallDetails,
  activityPriorDetails,
  timeOfIncidentDetails,
  shiftAndUnitDetails,
  actualVsProjectedDetails,
  caregiverAuditOverview,
  caregiverAuditDetails,
} from "../api/analytics/apiAnalytics";

interface Props {
  facility?: any;
  facilityIds?: string[] | string;
  incidentTypes?: any;
  residentStatus?: string[];
  skinIssueTypes?: string[];
  injuryTypes?: any;
  startDate?: any;
  endDate?: any;
  isClickedList?: boolean;
  isClickedTrend?: boolean;
  isClickedRepeat?: boolean;
  isClickedStatus?: boolean;
  isClickedWoundAnalysis?: boolean;
  isClickedBehaviorAnalysis?: boolean;
  isClickedIncidentRate?: boolean;
  isClickedInjuryReport?: boolean;
  day?: any;
  place?: any;
  createdBy?: any;
  createdById?: any;
  residentName?: any;
  residentId?: any;
  residentAction?: any;
  shift?: any;
  uShift?: any;
  unit?: any;
  monthStart?: any;
  monthEnd?: any;
  label?: string;
  grouping?: string;
  behaviorIncidentTypes?: string[];
  behaviorIncidentSubTypes?: string[];
  caregiverIds?: any;
  // Opt-in allow-list of query-key names this consumer actually needs. Omit for the
  // legacy behavior (every facility+date-gated query fires). Reports that need one
  // endpoint should pass it so they don't trigger the whole fall-analytics set.
  queries?: string[];
}

function useAnalytics({
  facilityIds,
  incidentTypes,
  residentStatus,
  skinIssueTypes,
  injuryTypes,
  startDate,
  endDate,
  isClickedList,
  isClickedStatus,
  isClickedTrend,
  isClickedRepeat,
  isClickedWoundAnalysis,
  isClickedBehaviorAnalysis,
  isClickedIncidentRate,
  isClickedInjuryReport,
  facility,
  day,
  place,
  createdBy,
  createdById,
  residentName,
  residentId,
  residentAction,
  shift,
  unit,
  uShift,
  monthStart,
  monthEnd,
  label,
  behaviorIncidentTypes,
  behaviorIncidentSubTypes,
  grouping,
  caregiverIds,
  queries,
}: Props = {}) {
  const enabledCommonConditions = !!facility && !!startDate && !!endDate;
  // No allow-list passed → every query behaves exactly as before.
  const wants = (name: string) => !queries || queries.includes(name);

  const analyticsIncidentList = useQuery(
    ["incidentList", facilityIds, incidentTypes, startDate, endDate],
    () =>
      getAnalyticsIncidentList(
        facilityIds as Array<string>,
        incidentTypes as Array<string>,
        startDate as string,
        endDate as string
      ),
    {
      enabled:
        !!isClickedList &&
        !!facilityIds &&
        !!incidentTypes &&
        !!startDate &&
        !!endDate,
    }
  );

  const analyticsIncidentTrend = useQuery(
    ["incidentTrend", facilityIds, startDate, endDate, incidentTypes, grouping],
    () =>
      getAnalyticsIncidentTrend(
        facilityIds as string,
        startDate as string,
        endDate as string,
        incidentTypes as string,
        grouping as string
      ),
    {
      enabled: !!isClickedTrend && !!facilityIds && !!startDate && !!endDate,
    }
  );

  const analyticsStatusReport = useQuery(
    ["statusReport", facilityIds, startDate, endDate],
    () =>
      getAnalyticsStatusReport(
        facilityIds as Array<string>,
        startDate as string,
        endDate as string
      ),
    {
      enabled: !!isClickedStatus && !!facilityIds && !!startDate && !!endDate,
    }
  );

  const analyticsRepeatIncident = useQuery(
    [
      "repeatIncident",
      facilityIds,
      startDate,
      endDate,
      incidentTypes,
      residentStatus,
    ],
    () =>
      getAnalyticsRepeatIncidents(
        facilityIds as Array<string>,
        startDate as string,
        endDate as string,
        incidentTypes as string,
        (residentStatus as string[]) || []
      ),
    {
      enabled:
        !!isClickedRepeat &&
        !!facilityIds &&
        !!startDate &&
        !!endDate &&
        !!incidentTypes,
    }
  );

  const analyticsWoundAnalysis = useQuery(
    ["woundAnalysis", facilityIds, startDate, endDate, skinIssueTypes],
    () =>
      getAnalyticsWoundAnalysis(
        facilityIds as Array<string>,
        startDate as string,
        endDate as string,
        skinIssueTypes as string[]
      ),
    {
      enabled:
        !!isClickedWoundAnalysis &&
        !!facilityIds &&
        !!startDate &&
        !!endDate &&
        !!skinIssueTypes,
    }
  );

  const analyticsBehaviorAnalysis = useQuery(
    ["behaviorAnalysis", facilityIds, startDate, endDate, behaviorIncidentTypes, behaviorIncidentSubTypes],
    () =>
      getAnalyticsBehaviorAnalysis(
        facilityIds as string[],
        startDate as string,
        endDate as string,
        behaviorIncidentTypes as string[],
        behaviorIncidentSubTypes as string[]
      ),
    {
      enabled:
        !!isClickedBehaviorAnalysis &&
        !!facilityIds &&
        !!startDate &&
        !!endDate &&
        !!behaviorIncidentTypes &&
        !!behaviorIncidentSubTypes,
    }
  );

  const analyticsIncidentRate = useQuery(
    ["incidentRate", facilityIds, startDate, endDate, incidentTypes, residentStatus],
    () =>
      incidentRateAnalysis(
        facilityIds as any,
        startDate as string,
        endDate as string,
        incidentTypes as string,
        (residentStatus as string[]) || []
      ),
    {
      enabled: !!isClickedIncidentRate && !!facilityIds && !!startDate && !!endDate,
      keepPreviousData: true,
    }
  );

  const analyticsInjuryReport = useQuery(
    ["injuryReport", facilityIds, startDate, endDate, incidentTypes, injuryTypes],
    () =>
      getAnalyticsInjuryReport(
        facilityIds as any,
        startDate as string,
        endDate as string,
        incidentTypes as string,
        injuryTypes as string
      ),
    {
      enabled:
        !!isClickedInjuryReport &&
        !!facilityIds &&
        !!startDate &&
        !!endDate &&
        !!incidentTypes &&
        !!injuryTypes,
    }
  );

  // new fall analysis

  const getIncidentListAnalysis = useQuery(
    ["incidentListAnalysis", facility, startDate, endDate],
    () => incidentListAnalysis(facility, startDate, endDate),
    {
      enabled: enabledCommonConditions && wants("incidentListAnalysis"),
      keepPreviousData: true,
    }
  );

  const getRepeatFallAnalysis = useQuery(
    ["repeatFallAnalysis", facility, startDate, endDate],
    () => repeatFallAnalysis(facility, startDate, endDate),
    {
      enabled: enabledCommonConditions && wants("repeatFallAnalysis"),
      keepPreviousData: true,
    }
  );

  const getActualVsProjectedFall = useQuery(
    ["actualVsProjectedFall", facility, startDate, endDate],
    () => actualVsProjectedFall(facility, startDate, endDate),
    {
      enabled: enabledCommonConditions && wants("actualVsProjectedFall"),
      keepPreviousData: true,
    }
  );

  const getFallIncidentPlace = useQuery(
    ["fallIncidentPlace", facility, startDate, endDate],
    () => fallIncidentPlace(facility, startDate, endDate),
    {
      enabled: enabledCommonConditions && wants("fallIncidentPlace"),
      keepPreviousData: true,
    }
  );

  const getActivityPriorIncient = useQuery(
    ["activityPriorIncident", facility, startDate, endDate],
    () => activityPriorIncident(facility, startDate, endDate),
    {
      enabled: enabledCommonConditions && wants("activityPriorIncident"),
      keepPreviousData: true,
    }
  );

  const getCaregiversReportingIncidents = useQuery(
    ["caregiversReportingIncidents", facility, startDate, endDate],
    () => careGiversReportingIncidents(facility, startDate, endDate),
    {
      enabled: enabledCommonConditions && wants("caregiversReportingIncidents"),
      keepPreviousData: true,
    }
  );

  // Caregiver Audit (MARCC-773): overview across the four roles + per-caregiver drill-down.
  // incidentTypes/caregiverIds are optional CSV filters — blank means "all".
  const getCaregiverAuditOverview = useQuery(
    [
      "caregiverAuditOverview",
      facility,
      startDate,
      endDate,
      incidentTypes,
      caregiverIds,
    ],
    () =>
      caregiverAuditOverview(
        facility,
        startDate,
        endDate,
        incidentTypes,
        caregiverIds
      ),
    {
      enabled: enabledCommonConditions && wants("caregiverAuditOverview"),
      keepPreviousData: true,
    }
  );

  const getCaregiverAuditDetails = useQuery(
    [
      "caregiverAuditDetails",
      facility,
      startDate,
      endDate,
      createdById,
      createdBy,
      incidentTypes,
    ],
    () =>
      caregiverAuditDetails(
        facility,
        startDate,
        endDate,
        createdById,
        createdBy,
        incidentTypes
      ),
    {
      enabled:
        enabledCommonConditions &&
        (!!createdById || !!createdBy) &&
        wants("caregiverAuditDetails"),
      keepPreviousData: true,
    }
  );

  const getOverviewRepeatfallIncidents = useQuery(
    ["overviewRepeatfallsIncidents", facility, startDate, endDate],
    () => overviewRepeatfallsIncidents(facility, startDate, endDate),
    {
      enabled: enabledCommonConditions && wants("overviewRepeatfallsIncidents"),
      keepPreviousData: true,
    }
  );

  const getOverviewDayOfWeekFallIncidentAnalysis = useQuery(
    ["overviewDayOfWeekFallIncidentAnalysis", facility, startDate, endDate],
    () => overviewDayOfWeekFallIncidentAnalytis(facility, startDate, endDate),
    {
      enabled: enabledCommonConditions && wants("overviewDayOfWeekFallIncidentAnalysis"),
      keepPreviousData: true,
    }
  );

  const getOverviewOfShiftAndUnitFallIncidents = useQuery(
    ["overviewOfShifAndUnitFallIncidents", facility, startDate, endDate],
    () => overviewOfShiftAndUnitFallIncidents(facility, startDate, endDate),
    {
      enabled: enabledCommonConditions && wants("overviewOfShifAndUnitFallIncidents"),
      keepPreviousData: true,
    }
  );

  const getOverviewTimeOfIncident2HourSpans = useQuery(
    ["overviewTimeOfIncident2HourSpans", facility, startDate, endDate],
    () => overviewTimeOfIncident2HourSpans(facility, startDate, endDate),
    {
      enabled: enabledCommonConditions && wants("overviewTimeOfIncident2HourSpans"),
      keepPreviousData: true,
    }
  );

  // inner pages
  const getDaysOfWeekDetails = useQuery(
    ["daysOfWeekDetails", facility, startDate, endDate, day],
    () => daysOfWeekDetails(facility, startDate, endDate, day),
    {
      enabled: enabledCommonConditions && !!day,
      keepPreviousData: true,
    }
  );

  const getPlaceOfIncidentDetails = useQuery(
    ["placeOfIncidentDetails", facility, startDate, endDate, place],
    () => placeOfIncidentDetails(facility, startDate, endDate, place),
    {
      enabled: enabledCommonConditions && !!place,
      keepPreviousData: true,
    }
  );

  const getActivityPriorDetails = useQuery(
    ["activityPriorDetails", facility, startDate, endDate, residentAction],
    () => activityPriorDetails(facility, startDate, endDate, residentAction),
    {
      enabled: enabledCommonConditions && !!residentAction,
      keepPreviousData: true,
    }
  );

  const getCaregiversReportingDetails = useQuery(
    [
      "caregiversReportingDetails",
      facility,
      startDate,
      endDate,
      createdById,
      createdBy,
    ],
    () =>
      caregiversReportingDetails(
        facility,
        startDate,
        endDate,
        createdById,
        createdBy
      ),
    {
      enabled: enabledCommonConditions && (!!createdById || !!createdBy),
      keepPreviousData: true,
    }
  );

  const getRepeatFallDetails = useQuery(
    ["repeatFallDetails", facility, startDate, endDate, residentId, residentName],
    () =>
      repeatFallDetails(facility, startDate, endDate, residentId, residentName),
    {
      enabled: enabledCommonConditions && (!!residentId || !!residentName),
      keepPreviousData: true,
    }
  );

  const getTimeOfIncidentDetails = useQuery(
    ["timeOfIncidentDetails", facility, startDate, endDate, shift],
    () => timeOfIncidentDetails(facility, startDate, endDate, shift),
    {
      enabled: enabledCommonConditions && !!shift,
      keepPreviousData: true,
    }
  );

  const getShiftAndUnitDetails = useQuery(
    ["shiftAndUnitDetails", facility, startDate, endDate, uShift, unit],
    () => shiftAndUnitDetails(facility, startDate, endDate, uShift, unit),
    {
      enabled: enabledCommonConditions && (!!uShift || !!unit),
      keepPreviousData: true,
    }
  );

  const getActualVsProjectedDetails = useQuery(
    ["actualVsProjectedDetails", facility, monthStart, monthEnd, label],
    () =>
      actualVsProjectedDetails(facility, monthStart, monthEnd, label as string),
    {
      enabled: !!facility && !!monthStart && !!monthEnd && !!label,
      keepPreviousData: true,
    }
  );

  return {
    analyticsIncidentList,
    analyticsIncidentTrend,
    analyticsRepeatIncident,
    analyticsStatusReport,
    analyticsWoundAnalysis,
    analyticsBehaviorAnalysis,
    analyticsIncidentRate,
    analyticsInjuryReport,
    getIncidentListAnalysis,
    getRepeatFallAnalysis,
    getActualVsProjectedFall,
    getFallIncidentPlace,
    getActivityPriorIncient,
    getCaregiversReportingIncidents,
    getCaregiverAuditOverview,
    getCaregiverAuditDetails,
    getOverviewRepeatfallIncidents,
    getOverviewDayOfWeekFallIncidentAnalysis,
    getOverviewOfShiftAndUnitFallIncidents,
    getOverviewTimeOfIncident2HourSpans,
    getDaysOfWeekDetails,
    getPlaceOfIncidentDetails,
    getCaregiversReportingDetails,
    getRepeatFallDetails,
    getActivityPriorDetails,
    getTimeOfIncidentDetails,
    getShiftAndUnitDetails,
    getActualVsProjectedDetails,
  };
}

export default useAnalytics;
